import { ActionTree } from 'vuex';
import { ForexTemplateState } from '@/feature/TrHistory/store/template/interface';
import { IStoreRootState } from '@/store/interface';
import {
	ForexTemplateAction,
	ForexTemplateGetter,
	ForexTemplateMutation
} from '@/feature/TrHistory/store/template/constants';
import {
	getTemplateCreateDTO,
	getTemplateFilter,
	getTemplateNextPageFilters
} from '@/feature/TrHistory/store/template/utils';
import {
	$createForexTemplate,
	$deleteForexTemplate,
	$getForexTemplateById,
	$getForexTemplateList,
	$updateForexTemplate
} from '@/feature/TrHistory/api/template/forex-template';
import { ForexTemplateFilter, ForexTemplateWithDraft } from '@/feature/TrHistory/api/template/forex-template.interface';
import { nextPageIsAvailable } from '@/feature/TrHistory/store/utils';

export default <ActionTree<ForexTemplateState, IStoreRootState>> {

	async [ForexTemplateAction.loadNextPage](context) {
		if (!nextPageIsAvailable(context, ForexTemplateGetter.loading))
			return [];

		return context.dispatch(
			ForexTemplateAction.loadPage,
			getTemplateNextPageFilters(context.state, context.commit)
		);
	},

	async [ForexTemplateAction.loadPage]({ state, commit }) {
		commit(ForexTemplateMutation.setLoading, true);

		const list = await $getForexTemplateList(getTemplateFilter(state));

		commit(ForexTemplateMutation.addList, list);
		commit(ForexTemplateMutation.setLoading, false);

		if (list.length < state.pageSize)
			commit(ForexTemplateMutation.setIsEndPage, true);

		return list;
	},

	async [ForexTemplateAction.reloadPage]({ state, commit, dispatch }) {
		commit(ForexTemplateMutation.resetPage);

		return dispatch(ForexTemplateAction.loadPage, getTemplateFilter(state));
	},

	async [ForexTemplateAction.loadAllPages]({ commit }, filter?: ForexTemplateFilter) {
		const list = await $getForexTemplateList(
			{
				...(filter || {}),
				pageNumber: 1,
				elementsOnPage: 100000
			},
			{ disableLoader: false }
		);

		commit(ForexTemplateMutation.setList, list);

		return list;
	},

	async [ForexTemplateAction.updateCurrentTemplate](
		{ commit, state },
		template?: ForexTemplateWithDraft
	) {
		const updated = await $updateForexTemplate({
			...(template || {}),
			templateId: state.currentTemplate.templateId
		});

		updated &&
			commit(ForexTemplateMutation.updateCurrentTemplate, template);

		return updated;
	},

	async [ForexTemplateAction.createTemplate](
		{ commit },
		template?: ForexTemplateWithDraft
	) {
		const createdTemplate = await $createForexTemplate(
			getTemplateCreateDTO(template)
		);

		createdTemplate?.templateId &&
			commit(ForexTemplateMutation.updateCurrentTemplate, createdTemplate);

		return createdTemplate;
	},

	async [ForexTemplateAction.deleteTemplate]({ commit }, templateId: number) {
		const deleted = await $deleteForexTemplate(templateId);

		deleted &&
			commit(ForexTemplateMutation.deleteTemplate, templateId);

		return deleted;
	},

	async [ForexTemplateAction.deleteCurrentTemplate]({ state, dispatch }) {
		return state.currentTemplate?.templateId
			? dispatch(
				ForexTemplateAction.deleteTemplate,
				state.currentTemplate.templateId
			)
			: false;
	},

	async [ForexTemplateAction.loadTemplate]({ commit }, templateId: number) {
		const template = await $getForexTemplateById(templateId);

		template &&
			commit(ForexTemplateMutation.setCurrentTemplate, template);

		return template;
	},

}
